<template>
    <div class="container-fluid">
        <Loading v-if="!loaded"></Loading>
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ title }}</h3>
            </div>
            <!-- LOG FIELDS -->
            <div class="row mt-4">
                <div class="col-sm-10 col-md-9 col-lg-8 col-xl-7">
                    <div class="form_group mb-3" v-for="row in logFields" v-bind:key="row.fieldName">
                        <label :for="row.fieldName" v-b-tooltip.hover:title="$t(row.tooltip)">
                            {{ $t(row.fieldLabel) }}
                        </label>
                        <div
                            type="text"
                            :id="row.fieldName"
                            class="form-control"
                            v-if="!row.useTextArea"
                            :style="getFormFieldStyle(row)"
                        >
                            <span v-if="row.fieldValueCorrected">
                                <strike>{{ row.fieldValue }}</strike> {{ row.fieldValueCorrected }}
                            </span>
                            <span v-else>{{ row.fieldValue }}</span>
                        </div>
                        <textarea
                            v-else
                            :id="row.fieldName"
                            class="form-control"
                            readonly
                            :style="{ backgroundColor: 'white' }"
                            v-model="row.fieldValue"
                        />
                    </div>
                    <!-- BUTTONS -->
                    <div class="d-flex flex-row mt-4">
                        <div class="d-flex flex-column mr-3">
                            <button class="btn btn-success" v-on:click="print()" :disabled="submitClicked">
                                {{ $t('print') }}
                            </button>
                        </div>
                        <div class="d-flex flex-column mr-3" v-if="user.role === 'pharmacy_owner' && !log.signature">
                            <button class="btn btn-info" v-on:click="handleSignLogClick" :disabled="submitClicked">
                                {{ $t('sign') }}
                            </button>
                        </div>
                        <div class="d-flex flex-column">
                            <input
                                type="button"
                                @click="returnToPreviousPage"
                                class="btn btn-secondary"
                                :value="$t('cancel')"
                                :disabled="submitClicked"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO SIGN LOG? -->
        <b-modal id="signConfirmationModal" centered :title="$t('sign_log_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('sign_log_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="signLog">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleSignLogCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import Loading from '@/views/partial/Loading';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { mapState } from 'vuex';
import { getLog, handleAxiosError, signLog } from '@/services/axiosService';
import { getLocalDate } from '@/services/dateService';
import { getAllLogFieldValues, getLatestLogFieldValue, getLogFieldValue } from '@/services/logService';
import { getMedDisplayAmount } from '@/services/medicationService';

export default {
    components: { Loading },
    data() {
        return {
            error: false,
            from: '',
            getAllLogFieldValues,
            getLatestLogFieldValue,
            getLocalDate,
            getLogFieldValue,
            getMedDisplayAmount,
            loaded: false,
            log: {},
            submitClicked: false
        };
    },
    // save previous page name for routing
    beforeRouteEnter(_to, from, next) {
        next(vm => {
            vm.from = from.name;
        });
        next();
    },
    async created() {
        await this.getLog();
        if (!this.error) {
            this.loaded = true;
        }
    },
    computed: {
        ...mapState('account', ['status', 'user', 'logout']),
        logFields() {
            return [
                // handler
                ...(this.log.user_name
                    ? [
                          {
                              fieldName: 'handler',
                              fieldLabel: 'handler',
                              fieldValue: this.log.user_name
                          }
                      ]
                    : []),
                // vnr
                ...(this.log.vnr ? [{ fieldName: 'vnr', fieldLabel: 'vnr', fieldValue: this.log.vnr }] : []),
                // medication name
                ...(this.log.medication_name
                    ? [
                          {
                              fieldName: 'medication_name',
                              fieldLabel: 'medication_name',
                              fieldValue: this.log.medication_name
                          }
                      ]
                    : []),
                // locker
                ...(this.log.locker
                    ? [
                          {
                              fieldName: 'locker',
                              fieldLabel: 'locker',
                              fieldValue: this.log.locker
                                  .split(',')
                                  .sort()
                                  .join(', ')
                          }
                      ]
                    : []),
                // amount changed (inventory correction)
                ...(this.hasAmount(this.log.action, this.log.amount_changed_packs, this.log.amount_changed_pills)
                    ? [
                          {
                              fieldName: 'amount_changed',
                              fieldLabel: this.getAmountTitle(
                                  this.log,
                                  this.log.amount_changed_packs >= 0 && this.log.amount_changed_pills >= 0
                              ),
                              fieldValue: this.getMedDisplayAmount(
                                  this.log.amount_changed_packs,
                                  this.log.amount_changed_pills,
                                  true,
                                  'short'
                              ),
                              tooltip: 'packages_pieces_tooltip'
                          }
                      ]
                    : []),
                // amount
                ...(this.hasAmount(this.log.action, this.log.amount, this.log.pills_amount)
                    ? [
                          {
                              fieldName: 'amount',
                              fieldLabel: this.getAmountTitle(this.log),
                              fieldValue: this.getMedDisplayAmount(
                                  this.log.amount,
                                  this.log.pills_amount,
                                  true,
                                  'short'
                              ),
                              tooltip: 'packages_pieces_tooltip'
                          }
                      ]
                    : []),
                // inventory
                ...(this.hasAmount(this.log.action, this.log.inventory_amount, this.log.partial_inventory_amount)
                    ? [
                          {
                              fieldName: 'inventory_amount',
                              fieldLabel: 'inventory_left',
                              fieldValue: this.getMedDisplayAmount(
                                  this.log.inventory_amount,
                                  this.log.partial_inventory_amount,
                                  true,
                                  'short'
                              ),
                              tooltip: 'packages_pieces_tooltip'
                          }
                      ]
                    : []),
                // description
                ...(this.log.description !== undefined
                    ? [
                          {
                              fieldName: 'description',
                              fieldLabel: 'description',
                              fieldValue: this.log.description
                          }
                      ]
                    : []),
                // reservation client
                ...(this.log.client && this.log.client.length
                    ? [
                          {
                              fieldName: 'client',
                              fieldLabel: 'client',
                              fieldValue: this.getLogFieldValue(this.log.client[0], this.log.type === 'take_out'),
                              fieldValuePrint: this.getLogFieldValue(
                                  this.log.client[0],
                                  this.log.type === 'take_out',
                                  true
                              ),
                              fieldValueCorrected: this.getCorrectedFieldValue(this.log.client)
                          }
                      ]
                    : []),
                // reservation doctor
                ...(this.log.doctor && this.log.doctor.length
                    ? [
                          {
                              fieldName: 'doctor',
                              fieldLabel: 'doctor',
                              fieldValue: this.getLogFieldValue(this.log.doctor[0], this.log.type === 'take_out'),
                              fieldValuePrint: this.getLogFieldValue(
                                  this.log.doctor[0],
                                  this.log.type === 'take_out',
                                  true
                              ),
                              fieldValueCorrected: this.getCorrectedFieldValue(this.log.doctor)
                          }
                      ]
                    : []),
                // reservation prescription
                ...(this.log.prescription && this.log.prescription.length
                    ? [
                          {
                              fieldName: 'prescription',
                              fieldLabel: 'prescription',
                              fieldValue: this.getLogFieldValue(this.log.prescription[0], this.log.type === 'take_out'),
                              fieldValuePrint: this.getLogFieldValue(
                                  this.log.prescription[0],
                                  this.log.type === 'take_out',
                                  true
                              ),
                              fieldValueCorrected: this.getCorrectedFieldValue(this.log.prescription)
                          }
                      ]
                    : []),
                // reservation ID
                ...(this.log.res_id
                    ? [
                          {
                              fieldName: 'res_id',
                              fieldLabel: 'reservation_id',
                              fieldValue: this.log.res_id
                          }
                      ]
                    : []),
                // comments
                ...(this.log.comments
                    ? [
                          {
                              fieldName: 'comments',
                              fieldLabel: 'comments',
                              fieldValue: this.getAllLogFieldValues(this.log.comments, true),
                              useTextArea: true
                          }
                      ]
                    : []),
                // 2D codes
                ...(this.log.two_d_codes
                    ? [
                          {
                              fieldName: 'two_d_codes',
                              fieldLabel: 'two_d_codes',
                              fieldValue: this.getDisplay2dCodes(this.log.two_d_codes, this.log.two_d_codes_invalid),
                              useTextArea: true
                          }
                      ]
                    : []),
                // created at
                ...(this.log.created_at
                    ? [
                        {
                            fieldName: 'created_at',
                            fieldLabel: 'created_at',
                            fieldValue: this.getLocalDate(this.log.created_at, this.$i18n.locale, true, true)
                        }
                    ]
                    : []),
                // updated at
                ...(this.log.updated_at
                    ? [
                        {
                            fieldName: 'updated_at',
                            fieldLabel: 'updated_at',
                            fieldValue: this.getLocalDate(this.log.updated_at, this.$i18n.locale, true, true)
                        }
                    ]
                    : []),
                // user and time if the drug was destroyed
                ...(this.log.mark_destroy_user
                    ? [
                        {
                            fieldName: 'mark_destroy_user_and_time',
                            fieldLabel: 'mark_destroy_user_and_time',
                            fieldValue: this.log.mark_destroy_user.first_name +
                                ' ' + this.log.mark_destroy_user.last_name + ' ' +
                                `${this.getLocalDate(
                                    this.log.time_destroyed_drugs,
                                    this.$i18n.locale,
                                    true,
                                    false
                                )}`
                        }
                    ]
                    : []),
                // signed by
                ...(this.log.signature
                    ? [
                          {
                              fieldName: 'signed_by',
                              fieldLabel: 'signed_by',
                              fieldValue: `${this.log.signature.web_user_name}${
                                  this.log.signature.created_at
                                      ? ` ${this.getLocalDate(
                                            this.log.signature.created_at,
                                            this.$i18n.locale,
                                            true,
                                            false
                                        )}`
                                      : ''
                              }`
                          }
                      ]
                    : [])
            ];
        },
        title() {
            return Object.keys(this.logFields).length
                ? `${this.$t('log')}: ${this.$t(this.log.action)} (${this.getLocalDate(
                      this.log.created_at,
                      this.$i18n.locale,
                      true,
                      true
                  )})`
                : this.$t('log');
        }
    },
    methods: {
        // get amount field title (depends on action)
        getAmountTitle(log, amountChangedPositive = null) {
            let title = '';
            if (
                log.type.match(/(take_out|reservation)/) ||
                (log.action === 'change' && amountChangedPositive === false)
            ) {
                title = 'amount_taken';
            } else if (log.type === 'put_in' || (log.action === 'change' && amountChangedPositive === true)) {
                title = 'amount_added';
            } else if (log.action === 'change') {
                title = 'amount_locker';
            }
            return title;
        },
        // format corrected field value for display
        getCorrectedFieldValue(fieldArray) {
            return fieldArray.length > 1 ? this.getLatestLogFieldValue(fieldArray, true) : '';
        },
        // format 2D codes for display
        getDisplay2dCodes(two_d_codes, two_d_codes_invalid) {
            let two_d_codes_all = [];

            const get2dCodes = input => {
                if (typeof input === 'string') {
                    return [input];
                } else if (typeof input === 'object') {
                    return input.filter(x => x);
                }
                return [];
            };

            if (two_d_codes) {
                two_d_codes_all = get2dCodes(two_d_codes);
            }
            if (!two_d_codes_all.length && two_d_codes_invalid) {
                two_d_codes_all = get2dCodes(two_d_codes_invalid);
            }
            return two_d_codes_all.join(', ');
        },
        // define form field style (height of correction-containing fields is static)
        getFormFieldStyle(row) {
            return row.fieldValueCorrected ? { height: '63px' } : {};
        },
        // get log
        async getLog() {
            try {
                this.log = await getLog(this.user.pharmacy_id, this.$route.params.id);
                this.log.id = this.$route.params.id;
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // handle "sign log" cancellation action
        handleSignLogCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('signConfirmationModal'); // hide "are you sure?" modal
        },
        // handle "sign log" click action
        handleSignLogClick() {
            this.submitClicked = true;
            this.$bvModal.show('signConfirmationModal'); // show "are you sure?" modal
        },
        // check if log involves amounts (i.e. changes to the inventory)
        hasAmount(action, amountField, pillsAmountField) {
            return (
                action.match(/^(put_in|take_out|change|reservation)$/) &&
                amountField !== undefined &&
                amountField !== null &&
                pillsAmountField !== undefined &&
                pillsAmountField !== undefined
            );
        },
        // print log as pdf
        print() {
            this.submitClicked = true;

            const lineHeight = 1.4;
            const doc = new jsPDF({
                format: 'a4',
                unit: 'pt',
                orientation: 'portrait',
                putOnlyUsedFonts: true,
                lineHeight
            });

            const margin = 55;
            let currentMargin = margin;
            const fontSizeBig = 15;
            const fontSizeNormal = 10;

            // filename
            let pdfName = `${this.$t('log')}__${this.$t(this.log.action)}__${this.getLocalDate(
                this.log.created_at,
                'en',
                true,
                true
            )}`
                .replace(/( | |:|\.)/g, '_')
                .replace(/('|´|\(|\))/g, '')
                .toLowerCase();
            doc.setProperties({ title: pdfName });

            // title
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(fontSizeBig);
            currentMargin = currentMargin + fontSizeBig - Math.floor(fontSizeBig / 4);
            doc.text(margin, currentMargin, this.title);

            // correction instructions
            doc.setFont('Helvetica', 'italic');
            doc.setFontSize(fontSizeNormal);
            currentMargin = currentMargin + fontSizeNormal * lineHeight + 10;
            doc.text(margin, currentMargin, this.$t('correction_legend'));
            doc.setFont('Helvetica', 'normal');
            currentMargin = currentMargin + fontSizeNormal * lineHeight - Math.floor(fontSizeNormal / 4);

            // content
            const columns = [[this.$t('field'), this.$t('value')]];
            const rows = [];
            for (const row of this.logFields) {
                const fieldValueCorrectedPrint = row.fieldValueCorrected
                    ? row.fieldValueCorrected.replace(/^ \[/, '- [').replace(/( \[)(?=[^\[]*$)/, '* [')
                    : null;

                rows.push([
                    this.$t(row.fieldLabel),
                    row.fieldValueCorrected
                        ? `${fieldValueCorrectedPrint} (${this.$t('original')}: ${row.fieldValuePrint ||
                              row.fieldValue})`
                        : row.fieldValue
                ]);
            }

            // generate table and open pdf
            doc.autoTable({
                margin: margin,
                startY: currentMargin,
                head: columns,
                body: rows
            });
            doc.autoPrint();
            // doc.output('dataurlnewwindow');
            window.open(doc.output('bloburl'));

            this.submitClicked = false;
        },
        // return to previous page (if logs index, retain previously used logs table filtering parameters)
        returnToPreviousPage() {
            if (this.from === 'logs.index') {
                this.$router.push({ name: this.from, params: { filterParams: this.$route.params.filterParams } });
            } else {
                this.$router.go(-1);
            }
        },
        // sign log
        async signLog() {
            try {
                this.$bvModal.hide('signConfirmationModal');
                const signData = await signLog(this.user.pharmacy_id, { logId: this.log.id });
                this.$toastr.s(this.$t('log_signed'));
                this.log = { ...this.log, ...signData }; // update shown log with signature data
            } catch (error) {
                handleAxiosError(error);
            }
            this.handleSignLogCancel();
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleSignLogCancel();
            }
        });
    }
};
</script>
